import React, { useCallback, useEffect, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Typography,
} from "@mui/material";
import { LoadingButton } from "@mui/lab";
import { Add } from "@mui/icons-material";
import { Controller, useForm } from "react-hook-form";
import Loading from "components/Loading";
import ConfirmDialog from "components/common/ConfirmDialog";
import DashboardPageHeader from "components/common/DashboardPageHeader";
import TciRegistrySelect, { useTciRegistryOptions } from "components/fde/TciRegistrySelect";
import InstitutionalCmrDataFlowPanel from "components/fde/InstitutionalCmrDataFlowPanel";
import { useAuth } from "hooks/useAuth";
import { manageDomain } from "services/fde/fdeService";
import { listAllClinicalDomains } from "services/fde/fdeRegistryService";

const EMPTY_FORM = {
  name: "",
  description: "",
  tciIds: [],
  tciManagerUid: "",
};

const DomainManagementPage = () => {
  const { user, organizationId, userData, isGlobalAdmin } = useAuth();
  const institutionId =
    userData?.activeInstitutionId ||
    userData?.institutionId ||
    organizationId ||
    null;
  const canManage =
    Boolean(isGlobalAdmin) ||
    (userData?.role === "admin" && Boolean(institutionId));

  const [domains, setDomains] = useState([]);
  const { tciNameById, reload: reloadTcis } = useTciRegistryOptions({ institutionId });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingDomain, setEditingDomain] = useState(null);
  const [deactivateTarget, setDeactivateTarget] = useState(null);
  const [deactivating, setDeactivating] = useState(false);

  const {
    control,
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm({ defaultValues: EMPTY_FORM });

  const loadData = useCallback(async () => {
    if (!institutionId) {
      setDomains([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const domainRows = await listAllClinicalDomains({ institutionId });
      setDomains(domainRows);
      await reloadTcis();
    } catch (err) {
      setError(err?.message || "Failed to load clinical domains.");
    } finally {
      setLoading(false);
    }
  }, [institutionId, reloadTcis]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const openCreate = () => {
    setEditingDomain(null);
    reset(EMPTY_FORM);
    setDialogOpen(true);
  };

  const openEdit = (domain) => {
    setEditingDomain(domain);
    reset({
      name: domain.name || "",
      description: domain.description || "",
      tciIds: Array.isArray(domain.tciIds) ? domain.tciIds : [],
      tciManagerUid: domain.tciManagerUid || "",
    });
    setDialogOpen(true);
  };

  const onSubmit = async (values) => {
    setSaving(true);
    setError(null);
    setSuccess(null);
    try {
      if (editingDomain) {
        await manageDomain({
          action: "update",
          domainId: editingDomain.id,
          name: values.name.trim(),
          description: values.description.trim(),
          tciIds: values.tciIds,
          tciManagerUid: values.tciManagerUid.trim() || null,
        });
        setSuccess("Domain updated.");
      } else {
        await manageDomain({
          action: "create",
          institutionId,
          name: values.name.trim(),
          description: values.description.trim(),
          tciIds: values.tciIds,
          tciManagerUid: values.tciManagerUid.trim() || null,
        });
        setSuccess("Domain created.");
      }
      setDialogOpen(false);
      await loadData();
    } catch (err) {
      setError(err?.message || "Failed to save domain.");
    } finally {
      setSaving(false);
    }
  };

  const handleDeactivate = async () => {
    if (!deactivateTarget) return;
    setDeactivating(true);
    setError(null);
    try {
      await manageDomain({ action: "deactivate", domainId: deactivateTarget.id });
      setSuccess("Domain deactivated.");
      setDeactivateTarget(null);
      await loadData();
    } catch (err) {
      setError(err?.message || "Failed to deactivate domain.");
    } finally {
      setDeactivating(false);
    }
  };

  if (!canManage) {
    return (
      <Alert severity="warning">
        You do not have permission to manage clinical domains.
      </Alert>
    );
  }

  return (
    <Box>
      <DashboardPageHeader
        title="Clinical domains"
        subtitle="Define institution-specific TCI groupings and assign TCI Managers."
        actions={
          <Button startIcon={<Add />} variant="contained" onClick={openCreate}>
            New domain
          </Button>
        }
      />

      {error ? (
        <Alert severity="error" sx={{ mt: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      ) : null}
      {success ? (
        <Alert severity="success" sx={{ mt: 2 }} onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      ) : null}

      {loading ? (
        <Loading page />
      ) : (
        <Box sx={{ mt: 3, display: "grid", gap: 2 }}>
          {!domains.length ? (
            <Alert severity="info">
              No clinical domains yet. Create one to group TCIs and assign a TCI Manager.
            </Alert>
          ) : (
            domains.map((domain) => (
              <Box
                key={domain.id}
                sx={{
                  p: 2,
                  border: "1px solid",
                  borderColor: "divider",
                  borderRadius: 1,
                  display: "grid",
                  gap: 1,
                }}
              >
                <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, alignItems: "center" }}>
                  <Typography variant="subtitle1" fontWeight={700}>
                    {domain.name}
                  </Typography>
                  <Chip
                    size="small"
                    label={domain.isActive ? "Active" : "Inactive"}
                    color={domain.isActive ? "success" : "default"}
                  />
                </Box>
                {domain.description ? (
                  <Typography variant="body2" color="text.secondary">
                    {domain.description}
                  </Typography>
                ) : null}
                <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.75 }}>
                  {(domain.tciIds || []).map((tciId) => (
                    <Chip key={tciId} size="small" label={tciNameById.get(tciId) || tciId} />
                  ))}
                </Box>
                <Typography variant="caption" color="text.secondary">
                  TCI Manager: {domain.tciManagerUid ? "Assigned" : "Not assigned"}
                </Typography>
                {domain.isActive ? (
                  <Box sx={{ display: "flex", gap: 1, mt: 0.5 }}>
                    <Button size="small" onClick={() => openEdit(domain)}>
                      Edit
                    </Button>
                    <Button
                      size="small"
                      color="error"
                      onClick={() => setDeactivateTarget(domain)}
                    >
                      Deactivate
                    </Button>
                  </Box>
                ) : null}
              </Box>
            ))
          )}
        </Box>
      )}

      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>{editingDomain ? "Edit domain" : "Create domain"}</DialogTitle>
        <DialogContent sx={{ display: "grid", gap: 2, pt: "8px !important" }}>
          <TextField
            label="Domain name"
            fullWidth
            error={Boolean(errors.name)}
            helperText={errors.name?.message}
            {...register("name", { required: "Name is required." })}
          />
          <TextField
            label="Description"
            fullWidth
            multiline
            minRows={2}
            {...register("description")}
          />
          <Controller
            name="tciIds"
            control={control}
            render={({ field }) => (
              <TciRegistrySelect
                label="TCIs"
                multiple
                institutionId={institutionId}
                value={field.value || []}
                onChange={field.onChange}
              />
            )}
          />
          <TextField
            label="TCI Manager account ID"
            fullWidth
            helperText="Firebase Auth user ID of the TCI Manager for this domain."
            {...register("tciManagerUid")}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)} disabled={saving}>
            Cancel
          </Button>
          <LoadingButton variant="contained" loading={saving} onClick={handleSubmit(onSubmit)}>
            Save
          </LoadingButton>
        </DialogActions>
      </Dialog>

      <ConfirmDialog
        open={Boolean(deactivateTarget)}
        onClose={() => setDeactivateTarget(null)}
        title="Deactivate domain?"
        message={`Deactivate "${deactivateTarget?.name || "this domain"}"? Existing role assignments are not removed.`}
        confirmLabel="Deactivate"
        confirmColor="error"
        loading={deactivating}
        onConfirm={handleDeactivate}
      />

      <InstitutionalCmrDataFlowPanel institutionId={institutionId} />
    </Box>
  );
};

export default DomainManagementPage;
